import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';

export interface GoogleAccountInfo {
  connected: boolean;
  email: string | null;
  user_id: string | null;
  refresh_token: string | null;
  sync_activities: boolean;
  sync_activities_calendar_id: string;
  google_calendar_ids: string[];
  scopes: string[];
}

export interface GoogleCalendar {
  id: string;
  summary: string;
  description?: string;
  primary?: boolean;
}

const INITIAL: GoogleAccountInfo = {
  connected: false,
  email: null,
  user_id: null,
  refresh_token: null,
  sync_activities: false,
  sync_activities_calendar_id: '',
  google_calendar_ids: [],
  scopes: [],
};

export function useGoogleAccount(userId: string | undefined, organizationId: string | undefined) {
  const [accountInfo, setAccountInfo] = useState<GoogleAccountInfo>(INITIAL);
  const [googleCalendars, setGoogleCalendars] = useState<GoogleCalendar[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchingCalendars, setFetchingCalendars] = useState(false);

  const refresh = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('google_account_connected, google_account_email, google_user_id, google_refresh_token, sync_activities, sync_activities_calendar_id, google_calendar_ids, google_workspace_scopes')
        .eq('id', userId)
        .single();

      if (error) throw error;

      const hasTokenAndId = !!(data.google_refresh_token && data.google_user_id);
      setAccountInfo({
        connected: !!data.google_account_connected && hasTokenAndId,
        email: data.google_account_email,
        user_id: data.google_user_id,
        refresh_token: data.google_refresh_token,
        sync_activities: !!data.sync_activities,
        sync_activities_calendar_id: data.sync_activities_calendar_id || '',
        google_calendar_ids: data.google_calendar_ids || [],
        scopes: data.google_workspace_scopes || [],
      });
    } catch (err) {
      console.error('[useGoogleAccount] Error fetching account:', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const fetchCalendars = useCallback(async () => {
    if (!userId || !organizationId) return;
    setFetchingCalendars(true);
    try {
      const { data, error } = await supabase.functions.invoke('google-calendar-list', {
        body: { organization_id: organizationId, user_id: userId },
      });
      if (error) throw error;
      if (data && Array.isArray(data.calendars)) {
        setGoogleCalendars(data.calendars);
      }
    } catch (err) {
      console.error('[useGoogleAccount] Error fetching calendars:', err);
    } finally {
      setFetchingCalendars(false);
    }
  }, [userId, organizationId]);

  const disconnect = useCallback(async () => {
    if (!userId) return;
    const { error } = await supabase
      .from('user_profiles')
      .update({
        google_account_connected: false,
        google_refresh_token: null,
        google_account_email: null,
        google_user_id: null,
      })
      .eq('id', userId);

    if (!error) {
      setAccountInfo(INITIAL);
      setGoogleCalendars([]);
    }
    return !error;
  }, [userId]);

  const updateUserProfile = useCallback(async (updates: Partial<{
    sync_activities: boolean;
    sync_activities_calendar_id: string;
    google_calendar_ids: string[];
  }>) => {
    if (!userId) return false;
    const { error } = await supabase
      .from('user_profiles')
      .update(updates)
      .eq('id', userId);
    if (!error) {
      setAccountInfo(prev => ({ ...prev, ...updates }));
    }
    return !error;
  }, [userId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Auto-fetch calendars when connected
  useEffect(() => {
    if (accountInfo.connected && googleCalendars.length === 0 && !fetchingCalendars) {
      fetchCalendars();
    }
  }, [accountInfo.connected, googleCalendars.length, fetchingCalendars, fetchCalendars]);

  return {
    accountInfo,
    googleCalendars,
    loading,
    fetchingCalendars,
    refresh,
    fetchCalendars,
    disconnect,
    updateUserProfile,
  };
}
